<template>
<div class="m04" :class="{ 'm04--modus': (sitzung && !fertig) || (!sitzung && art && schemaGewaehlt) }">

    <!-- Sitzung: alle Uebungen bearbeitet -->
    <template v-if="sitzung && fertig">
        <p class="pfad">Sitzung</p>
        <h1>Alle Übungen bearbeitet</h1>
        <p class="hinweis">
            Du hast alle {{ uebungen.length }} Übungen dieser Sitzung bearbeitet.
            Dein Ausbildner sieht deinen Stand.
        </p>
        <RouterLink :to="{ name: 'Training' }" class="rechts">Zur Übersicht</RouterLink>
    </template>

    <!-- Auswahl, Stufe 1: Uebungsart. Nicht in der Sitzung — dort hat der
         Ausbildner gewaehlt. Die Wahl steht in der Adresse (?art=…&kategorie=…),
         damit "Zurueck" im Browser und Neuladen funktionieren. -->
    <template v-else-if="!sitzung && !art">
        <p class="pfad">Sprechregeln</p>
        <h1>Übungsart wählen</h1>
        <p class="hinweis">Von links nach rechts: vom Erkennen bis zum selbst Funken.</p>
        <!-- Die vier Stufen als Spalten nebeneinander: der Weg ist sichtbar. -->
        <div class="stufen">
            <section v-for="(st, i) in stufen" :key="st.id" class="stufen__spalte" :aria-labelledby="'stufe-' + st.id">
                <h2 :id="'stufe-' + st.id" class="stufe"><span class="stufe__nr">{{ i + 1 }}</span>{{ st.name }}</h2>
                <nav class="auswahl">
                    <RouterLink v-for="a in uebungsarten.filter(a => a.stufe === st.id)" :key="a.id" :to="{ query: { art: a.id } }" class="karte">
                        <span class="titel">{{ a.name }}</span>
                        <span class="beschreibung">{{ a.beschreibung }}</span>
                    </RouterLink>
                </nav>
            </section>
        </div>
    </template>

    <!-- Auswahl, Stufe 2: Kategorie -->
    <template v-else-if="!sitzung && !schemaGewaehlt">
        <p class="pfad"><RouterLink :to="{ query: {} }">Sprechregeln</RouterLink> / {{ art.name }}</p>
        <h1>Kategorie wählen</h1>
        <p class="hinweis">{{ art.beschreibung }}</p>
        <!-- Spalten nach Zahl der sprechenden Stationen, von links nach
             rechts vom Einfachen zum Vielstimmigen (wie die Stufen bei
             den Uebungsarten). Die Zahl im Kreis ist diese Anzahl. -->
        <div class="kategorie-spalten">
        <section v-for="g in kategorieGruppen" :key="g.id" class="kategorie-spalte" :aria-labelledby="'gruppe-' + g.id">
        <h2 :id="'gruppe-' + g.id" class="stufe"><span class="stufe__nr">{{ g.marke }}</span>{{ g.name }}</h2>
        <nav class="kategorien" :aria-label="g.name">
            <!-- Flip-Karte: vorne die Kategorie, hinten ein Beispielablauf.
                 Gedreht wird per Knopf (auch Tipp und Tastatur), nie beim
                 Darueberfahren. Die abgewandte Seite ist inert: nicht
                 fokussierbar, nicht vorgelesen. -->
            <div v-for="s in g.liste" :key="s.kategorie"
                 class="kategorie" :class="{ gedreht: gedreht === s.kategorie }">
                <div class="kategorie__innen">

                    <div class="kategorie__seite kategorie__vorne" :inert="gedreht === s.kategorie || null">
                        <RouterLink :to="{ query: { art: art.id, kategorie: s.kategorie } }" class="kategorie__band">
                            <span class="titel">{{ s.name }}</span>
                            <span class="kategorie__pfeil" aria-hidden="true">→</span>
                        </RouterLink>
                        <span class="beschreibung">{{ s.beschreibung }}</span>
                        <span class="kategorie__etiketten">
                            <span class="etikett">{{ s.zuege.length }} {{ s.zuege.length === 1 ? 'Spruch' : 'Sprüche' }}</span>
                        </span>
                        <button :id="'drehen-' + s.kategorie" class="kategorie__drehen" @click="drehen(s.kategorie)">
                            Beispiel ansehen
                        </button>
                    </div>

                    <div class="kategorie__seite kategorie__hinten" :inert="gedreht !== s.kategorie || null">
                        <p :id="'beispiel-' + s.kategorie" class="kategorie__hinten-titel" tabindex="-1">
                            Beispiel · {{ s.name }}
                        </p>
                        <ol class="beispiel">
                            <li v-for="z in beispiele[s.kategorie].zuege" :key="z.nr">
                                <span class="beispiel__station">{{ z.station.rufname }}</span>
                                <span class="beispiel__spruch">{{ z.teile.map(t => t.text).join(' ') }}</span>
                            </li>
                        </ol>
                        <span class="kategorie__hinten-fuss">
                            <button class="kategorie__zurueck" @click="zurueckDrehen(s.kategorie)">Zurück</button>
                            <RouterLink :to="{ query: { art: art.id, kategorie: s.kategorie } }" class="kategorie__ueben">Üben →</RouterLink>
                        </span>
                    </div>

                </div>
            </div>
        </nav>
        </section>
        </div>
    </template>

    <!-- Uebungsmodus, angelehnt an die Layoutskizzen in Import/UI-Layout:
         Kopfzeile mit «Beenden» (D), Lernzielband (B), in der Mitte ein
         einzelner Kasten mit der Uebung und der Knopfleiste unten (D).
         Die Leiste der App ist hier ausgeblendet (App.vue). -->
    <div v-else class="modus">

    <header class="modus__kopf">
        <RouterLink v-if="!sitzung" :to="{ query: { art: art.id } }" class="modus__beenden">Beenden</RouterLink>
        <RouterLink v-else :to="{ name: 'SitzungBeitreten' }" class="modus__beenden">Beenden</RouterLink>
        <div class="modus__titel">
            <span class="modus__art">{{ art.name }}</span>
            <span class="modus__kategorie">{{ sitzung ? 'Sitzung' : schemaGewaehlt.name }}</span>
        </div>
        <p v-if="sitzung" class="modus__zaehler">
            Übung {{ index + 1 }} von {{ uebungen.length }}<template v-if="schuelerSitzung.daten"> · {{ schuelerSitzung.daten.name }}</template>
        </p>
    </header>

    <div class="modus__ziel">
        <div class="modus__ziel-text">
            <p class="modus__etikett">Lernziel</p>
            <p class="modus__lernziel">{{ art.lernziel }}</p>
        </div>

        <!-- Ablenker-Schalter. Umschalten baut eine neue Uebung: Bausteine, die
             mitten in einem halb gelegten Spruch verschwinden, waeren nicht
             nachvollziehbar. Nur beim Sortieren — die Fehlersuche nimmt ihre
             Fehler ohnehin aus den Ablenkern. -->
        <label v-if="!sitzung && art.id === 'allesSortieren'" class="schalter">
            <!-- Ein Schalter, kein Haekchen: er stellt die Uebung um. -->
            <input type="checkbox" role="switch" class="schalter__knopf" v-model="mitAblenkern" @change="neueUebung()">
            Mit Ablenkern
        </label>

        <!-- Rolle bei "Reagieren". Umschalten baut eine neue Uebung. In der
             Sitzung waehlt der Ausbildner. Alle drei Rollen sichtbar, ein
             Klick (Segmentknopf statt Liste). -->
        <div v-if="!sitzung && art.id === 'reagieren'" class="schalter" role="group" aria-labelledby="rolle-titel">
            <span id="rolle-titel">Du sprichst als</span>
            <span class="segmente">
                <button v-for="r in rollen" :key="r.id" :aria-pressed="rolle === r.id" @click="rolleWaehlen(r.id)">
                    {{ r.name }}
                </button>
            </span>
        </div>
    </div>

    <div v-if="sitzung" class="modus__reihe">
        <ZugStepper wort="Übung" :anzahl="uebungen.length" :aktuell="index" />
    </div>

    <div class="modus__mitte" :class="{ 'modus__mitte--breit': art.id === 'reagieren' }">

    <p v-if="sitzung && sitzungBeendet" class="sitzung-beendet" role="status">
        Die Sitzung ist beendet. Du kannst weiterüben, dein Ausbildner sieht den Stand aber nicht mehr.
    </p>

    <!-- Lage-Karte. Bei "Reagieren" steht sie stattdessen oben in der
         klebenden Bausteinspalte, damit sie bei langem Verlauf sichtbar bleibt. -->
    <p v-if="art.id !== 'reagieren'" class="lage"><span class="lage__etikett">Lage</span>{{ uebung.kontext }}</p>

    <!-- Die Uebung selbst. Welche Ansicht, entscheidet die Uebungsart.
         :key = neue Uebung, neue Instanz — kein Zustand wird mitgeschleppt. -->
    <!-- Das Ablaufgitter behaelt seine eigene Knopfleiste (bleibt unveraendert). -->
    <div class="arbeitsflaeche" :class="{ 'mit-fussleiste': art.id !== 'ablaufgitter' }">
    <component
        :is="ansichten[art.id]"
        :key="durchgang"
        :uebung="uebung"
        :mit-ablenkern="mitAblenkern"
        :leer-sperren="sitzung"
        @geprueft="geprueft">
        <template #rechts>
            <template v-if="sitzung">
                <button v-if="geloest" @click="naechsteUebung()" class="rechts">Weiter</button>
                <button v-else @click="ueberspringen()" class="rechts">Überspringen</button>
            </template>
            <button v-else @click="neueUebung()" class="rechts">Neue Übung</button>
        </template>
    </component>
    </div>

    </div>
    </div>

</div>
</template>

<script>
import { baueUebung } from '@/logic/Uebung';
import { bereiteVor } from '@/logic/Fehler';
import { inventar, schemata } from '@/data/daten';
import { ref, computed, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { schuelerSitzung, aufgabenFesthalten, standMelden } from '@/logic/sitzungSchueler';
import { ziehenSprechregeln } from '@/logic/sitzungAufgaben';
import { uebungsarten, stufen, passt } from '@/data/Uebungsarten';
import { stationenVon } from '@/logic/Einzug';
import EinfachSortieren from '@/components/EinfachSortieren.vue';
import AblaufGitter from '@/components/AblaufGitter.vue';
import AllesSortieren from '@/components/AllesSortieren.vue';
import FassungWaehlen from '@/components/FassungWaehlen.vue';
import FehlerMarkieren from '@/components/FehlerMarkieren.vue';
import RegelBenennen from '@/components/RegelBenennen.vue';
import FehlerKorrigieren from '@/components/FehlerKorrigieren.vue';
import Reagieren from '@/components/Reagieren.vue';
import ZugStepper from '@/components/ZugStepper.vue';

// Eine Ansicht pro Uebungsart. Eine neue Uebungsart = eine Komponente und
// eine Zeile hier. Jede haelt denselben Vertrag: Prop "uebung", Event
// "geprueft" mit { sitzt, geloest }, Slot "rechts" fuer die Knoepfe von hier.
// Die Ansichten der Fehlersuche bauen ihre Fehler selbst in die Uebung ein.
const ansichten = {
    einfachSortieren: EinfachSortieren,
    ablaufgitter: AblaufGitter,
    allesSortieren: AllesSortieren,
    fassungWaehlen: FassungWaehlen,
    fehlerMarkieren: FehlerMarkieren,
    regelBenennen: RegelBenennen,
    fehlerKorrigieren: FehlerKorrigieren,
    reagieren: Reagieren
}

export default {
    components: { ZugStepper },
    // sitzung: true unter /sitzung/sprechregeln. Dann kommen die Uebungen aus der
    // Sitzung, Pruefen wird gezaehlt, und statt "Neue Uebung" gibt es
    // "Ueberspringen" bzw. "Weiter".
    // Alles, was die Sitzung betrifft, steht HIER und nur hier — die
    // Ansichten der Uebungsarten wissen nichts davon.
    props: {
        sitzung: {type: Boolean, default: false}
    },
    setup(props) {
        // --- Sitzung ---
        // Einmal gezogen und gemerkt; nach einem Neuladen geht es bei der
        // ersten offenen Uebung weiter.
        let uebungen = []
        const index = ref(0)
        if (props.sitzung) {
            const d = schuelerSitzung.daten
            if (!d.aufgaben) {
                const { aufgaben, stand } = ziehenSprechregeln(d.konfiguration)
                aufgabenFesthalten(aufgaben, stand)
            }
            uebungen = d.aufgaben
            const ersteOffene = d.stand.findIndex(e => e.ergebnis === 'offen')
            index.value = ersteOffene === -1 ? uebungen.length : ersteOffene
        }
        const fertig = computed(() => index.value >= uebungen.length)
        const sitzungBeendet = computed(() => schuelerSitzung.beendet)

        // Aendert den Eintrag der aktuellen Uebung und meldet den ganzen Stand.
        const eintragAendern = (aenderung) => {
            const stand = schuelerSitzung.daten.stand.map((e, i) =>
                i === index.value ? {...e, ...aenderung(e)} : e)
            standMelden(stand)
        }

        // --- Auswahl ---
        // Steht in der Adresse. Unbekannte Werte zaehlen wie fehlende — dann
        // zeigt die Seite wieder das Menue statt eines Fehlers.
        // In der Sitzung gibt es kein Menue: die Uebungen kommen fertig aus
        // der Sitzung und tragen ihre Uebungsart selbst ("art"). Uebungen aus
        // einer Sitzung von vor den Uebungsarten haben keine — das war immer
        // "Alles sortieren".
        const route = useRoute()
        const art = computed(() => props.sitzung
            ? uebungsarten.find(a => a.id === (uebung.value.art ?? 'allesSortieren'))
            : uebungsarten.find(a => a.id === route.query.art) ?? null)
        // Nur die Ablaeufe, die mit der Uebungsart gehen — auch wenn eine
        // unpassende Kategorie in der Adresse steht.
        const schemataFuerArt = computed(() => art.value
            ? schemata.filter(s => passt(art.value.id, s))
            : [])
        const schemaGewaehlt = computed(() => (props.sitzung || !art.value)
            ? null
            : schemataFuerArt.value.find(s => s.kategorie === route.query.kategorie) ?? null)

        // --- Kategoriekarten ---
        // Spalten nach Zahl der sprechenden Stationen (1, 2, 3+), aus den
        // Zuegen jedes Schemas berechnet: eine neue Kategorie ordnet sich
        // selbst ein. Leere Spalten fallen weg.
        const kategorieGruppen = computed(() => {
            const gruppen = [
                { id: 'eine', marke: '1', name: 'Eine Station spricht', liste: [] },
                { id: 'zwei', marke: '2', name: 'Zwei Stationen', liste: [] },
                { id: 'mehr', marke: '3+', name: 'Drei und mehr Stationen', liste: [] }
            ]
            for (const s of schemataFuerArt.value) {
                const n = stationenVon(s).length
                gruppen[n <= 1 ? 0 : n === 2 ? 1 : 2].liste.push(s)
            }
            return gruppen.filter(g => g.liste.length > 0)
        })

        // Rueckseite: ein Beispielablauf pro Kategorie, einmal gebaut, sobald
        // die Uebungsart feststeht. Gedreht ist immer hoechstens eine Karte.
        const beispiele = computed(() => Object.fromEntries(
            schemataFuerArt.value.map(s => [s.kategorie, baueUebung(s, inventar)])))
        const gedreht = ref(null)

        // Der Fokus wandert mit: die abgewandte Seite ist inert, ein Fokus
        // dort ginge sonst verloren.
        const drehen = async (kategorie) => {
            gedreht.value = kategorie
            await nextTick()
            document.getElementById('beispiel-' + kategorie)?.focus()
        }
        const zurueckDrehen = async (kategorie) => {
            gedreht.value = null
            await nextTick()
            document.getElementById('drehen-' + kategorie)?.focus()
        }

        // --- Uebung ---
        // Rolle bei "Reagieren" (Schalter auf der Seite); die Sitzung hat ihre
        // Uebungen schon fertig gezogen.
        const rolle = ref('zufall')
        const uebung = ref(props.sitzung
            ? (uebungen[index.value] ?? uebungen[0])
            : (schemaGewaehlt.value ? bereiteVor(art.value.id, baueUebung(schemaGewaehlt.value, inventar), rolle.value) : null))
        // In der Sitzung entscheidet der Ausbildner (konfiguration.ablenker),
        // sonst der Schalter auf der Seite. Die Ablenker selbst stecken schon
        // in jeder gezogenen Uebung, auch in den gemerkten der Sitzung.
        const mitAblenkern = ref(props.sitzung
            ? schuelerSitzung.daten.konfiguration.ablenker === true
            : false)

        // Zaehlt hoch bei jeder neuen Uebung und dient als :key der Ansicht.
        const durchgang = ref(0)
        // Bleibt true, bis die naechste Uebung kommt — auch wenn danach noch
        // Bausteine verschoben werden. Sonst wuerde aus "Weiter" wieder
        // "Ueberspringen", und das wuerde ein "richtig" ueberschreiben.
        const geloest = ref(false)

        const zeigen = (neu) => {
            uebung.value = neu
            geloest.value = false
            durchgang.value += 1
        }

        // bereiteVor wuerfelt, was die Uebungsart braucht (Fehler, Fassungen …).
        const neueUebung = () => zeigen(bereiteVor(art.value.id, baueUebung(schemaGewaehlt.value, inventar), rolle.value))

        // Rolle bei "Reagieren": nur ein Wechsel baut eine neue Uebung.
        const rollen = [
            { id: 'zufall', name: 'zufällig' },
            { id: 'rufer', name: 'rufende Station' },
            { id: 'gegenstation', name: 'Gegenstation' }
        ]
        const rolleWaehlen = (id) => {
            if (rolle.value === id) return
            rolle.value = id
            neueUebung()
        }

        // Andere Kategorie gewaehlt (auch: zurueck ins Menue und wieder
        // hinein) — die Seite bleibt dieselbe Instanz, also hier neu bauen.
        watch(schemaGewaehlt, (s) => { if (s) neueUebung() })

        // Sitzung: gezaehlt wird jedes FALSCHE Pruefen, und einmal das Pruefen,
        // das die Uebung loest. Ein richtig gelegter Zwischenzug zaehlt nicht —
        // sonst braeuchte ein Ablauf mit drei Zuegen mindestens drei
        // Pruefungen, und die Lehreransicht ("richtig beim 1. Mal" heisst
        // pruefungen <= 1) koennte ihn nie als richtig zeigen.
        // Ist die Uebung geloest, aendert erneutes Pruefen nichts mehr.
        const geprueft = ({ sitzt, geloest: jetztGeloest }) => {
            if (jetztGeloest) geloest.value = true
            const zaehlt = !sitzt || jetztGeloest
            if (props.sitzung && zaehlt && schuelerSitzung.daten.stand[index.value].ergebnis === 'offen') {
                eintragAendern(e => ({
                    pruefungen: e.pruefungen + 1,
                    ergebnis: jetztGeloest ? 'richtig' : 'offen'
                }))
            }
        }

        const naechsteUebung = () => {
            index.value += 1
            if (fertig.value) return
            zeigen(uebungen[index.value])
        }

        const ueberspringen = () => {
            eintragAendern(() => ({ ergebnis: 'uebersprungen' }))
            naechsteUebung()
        }

        return { ansichten, uebungsarten, stufen, schemataFuerArt, art, schemaGewaehlt,
                 uebung, mitAblenkern, rolle, durchgang, geloest,
                 neueUebung, geprueft, naechsteUebung, ueberspringen,
                 uebungen, index, fertig, sitzungBeendet, stationenVon,
                 beispiele, gedreht, drehen, zurueckDrehen, kategorieGruppen,
                 rollen, rolleWaehlen, schuelerSitzung }
    }
}

</script>

<style scoped>
/* #app zentriert global allen Text — hier wieder abwaehlen. */
.m04 {
    max-width: 1120px;
    padding: 2rem 2.5rem 3rem;
    text-align: left;
}

/* ─── Kopf jeder Ansicht: Pfad, dann Titel ────────────────── */

.pfad {
    margin: 0 0 0.35rem;
    font-size: 0.75rem;
    font-weight: 650;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--zs-grau);
}

.pfad a {
    color: inherit;
    text-underline-offset: 3px;
}

.pfad a:hover {
    color: var(--zs-blau);
}

/* ─── Uebungsmodus ────────────────────────────────────────
   Volle Breite, eigene Kopfzeile, die Uebung als ein Kasten in der Mitte. */
.m04--modus {
    max-width: none;
    padding: 0;
}

.modus__kopf {
    position: sticky;
    top: 0;
    z-index: 40;
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 2rem;
    background: var(--zs-tafel);
    border-bottom: 1px solid var(--zs-rahmen);
}

.modus__beenden {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    min-height: 2.5rem;
    padding: 0.4rem 1rem;
    font-weight: 600;
    color: var(--zs-tinte);
    text-decoration: none;
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
    box-sizing: border-box;
}

.modus__beenden::before {
    content: '←';
    color: var(--zs-grau);
}

.modus__beenden:hover {
    background: var(--zs-flaeche);
}

.modus__titel {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.modus__art {
    font-size: 1.05rem;
    font-weight: 700;
}

.modus__kategorie {
    font-size: 0.85rem;
    color: var(--zs-grau);
}

.modus__zaehler {
    margin: 0 0 0 auto;
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--zs-grau);
}

/* Lernzielband: was hier geuebt wird, als Ich-Satz. Rechts die Schalter. */
.modus__ziel {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem 2rem;
    padding: 1rem 2rem 1.1rem;
    background: var(--zs-tafel);
    border-bottom: 1px solid var(--zs-rahmen);
}

.modus__etikett {
    margin: 0 0 0.2rem;
    font-size: 0.72rem;
    font-weight: 650;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--zs-grau);
}

.modus__lernziel {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 600;
    color: var(--zs-tinte);
}

/* Warteschlange der Sitzung als Kachelreihe. */
.modus__reihe {
    padding: 0.85rem 2rem 0;
}

.modus__reihe :deep(.stepper) {
    margin: 0;
}

/* Die Uebung bleibt schmal: Spruchzeilen sollen nicht ueber die ganze
   Breite laufen. */
.modus__mitte {
    max-width: 880px;
    margin: 0 auto;
    padding: 2rem 2rem 3rem;
}

/* Reagieren hat zwei Spalten (Arbeit + Bausteine) und braucht mehr Breite. */
.modus__mitte--breit {
    max-width: 1180px;
}

h1 {
    margin: 0 0 1.25rem;
    font-size: 2rem;
    font-weight: 750;
    line-height: 1.15;
    letter-spacing: -0.015em;
    color: var(--zs-tinte);
    text-wrap: balance;
}

.hinweis {
    margin: 0 0 2rem;
    color: var(--zs-grau);
}

.sitzung-beendet {
    margin: 0 0 1rem;
    padding: 0.65rem 0.9rem;
    color: var(--zs-warnrot);
    background: var(--zs-warnrot-hell);
    border: 1px solid rgba(179, 38, 30, 0.35);
    border-radius: var(--zs-ecke);
}

/* ─── Auswahl ─────────────────────────────────────────────
   Vier Spalten, eine pro Stufe, von links nach rechts. In jeder Spalte
   die Uebungsarten als Karten. */

.stufen {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(13.5rem, 1fr));
    gap: 1.25rem;
    align-items: start;
}

.stufe {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin: 0 0 0.75rem;
    font-size: 0.8rem;
    font-weight: 650;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--zs-grau);
}

.stufe__nr {
    display: grid;
    place-items: center;
    min-width: 1.6rem;
    height: 1.6rem;
    padding: 0 0.3rem;
    box-sizing: border-box;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0;
    color: var(--zs-blau);
    background: var(--zs-blau-hell);
    border-radius: 999px;
}

.auswahl {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.karte {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.3rem 0.75rem;
    padding: 1rem 1.1rem 1.1rem;
    color: var(--zs-tinte);
    text-decoration: none;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke-gross);
    box-shadow: var(--zs-schatten);
    transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
}

.karte::after {
    content: '→';
    grid-column: 2;
    grid-row: 1;
    color: var(--zs-grau);
    transition: transform 0.15s, color 0.15s;
}

.karte:hover {
    border-color: var(--zs-blau-mittel);
    box-shadow: var(--zs-schatten-hoch);
    transform: translateY(-2px);
}

.karte:hover::after {
    color: var(--zs-blau);
    transform: translateX(2px);
}

.karte:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

.titel {
    font-size: 1.05rem;
    font-weight: 650;
    line-height: 1.3;
}

.beschreibung {
    grid-column: 1 / -1;
    font-size: 0.92rem;
    line-height: 1.45;
    color: var(--zs-grau);
}

/* ─── Kategorien als Flip-Karten ──────────────────────────
   Vorne: helles Kopfband, Beschreibung, Etiketten, Knopf «Beispiel
   ansehen». Hinten: ein Beispielablauf in Funkschrift. Idee nach der
   Flip Card von Inspira UI, ohne Paket: perspective + rotateY +
   backface-visibility. Beide Seiten liegen in derselben Rasterzelle,
   die Karte ist so hoch wie die laengere Seite. */

/* Spalten nach Zahl der Stationen; in jeder Spalte die Karten
   untereinander. */
.kategorie-spalten {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(19rem, 1fr));
    gap: 1.25rem;
    align-items: start;
}

.kategorien {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.kategorie {
    perspective: 1400px;
}

.kategorie__innen {
    display: grid;
    height: 100%;
    transform-style: preserve-3d;
    transition: transform 0.55s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.kategorie.gedreht .kategorie__innen {
    transform: rotateY(180deg);
}

.kategorie__seite {
    grid-area: 1 / 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    color: var(--zs-tinte);
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
    overflow: hidden;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    transition: border-color 0.15s, box-shadow 0.15s;
}

.kategorie__hinten {
    transform: rotateY(180deg);
}

/* Vorderseite: die ganze Flaeche oeffnet die Uebung (der Link im Band
   spannt sich ueber die Karte), nur der Knopf liegt darueber. */
.kategorie__vorne {
    position: relative;
}

.kategorie__vorne:hover {
    border-color: rgba(0, 51, 160, 0.45);
    box-shadow: 0 10px 24px -14px rgba(11, 31, 77, 0.35);
}

.kategorie__band {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 1rem;
    padding: 1.1rem 1.35rem;
    color: inherit;
    text-decoration: none;
    background: var(--zs-blau-hell);
    border-bottom: 1px solid rgba(0, 51, 160, 0.12);
    transition: background-color 0.15s;
}

.kategorie__band::after {
    content: '';
    position: absolute;
    inset: 0;
}

.kategorie__vorne:hover .kategorie__band {
    background: #DCE4F4;
}

.kategorie__band:focus-visible {
    outline: none;
}

.kategorie__band:focus-visible::after {
    outline: 3px solid var(--zs-orange);
    outline-offset: -3px;
    border-radius: var(--zs-ecke);
}

.kategorie__band .titel {
    font-size: 1.2rem;
    font-weight: 700;
    line-height: 1.25;
    color: var(--zs-blau);
}

.kategorie__pfeil {
    flex: none;
    font-weight: 700;
    color: var(--zs-blau);
    transition: transform 0.15s;
}

.kategorie__vorne:hover .kategorie__pfeil {
    transform: translateX(3px);
}

.kategorie__vorne > .beschreibung {
    padding: 1.1rem 1.35rem 0.5rem;
    font-size: 1rem;
    line-height: 1.55;
}

.kategorie__etiketten {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    padding: 0.75rem 1.35rem 1rem;
}

.etikett {
    padding: 0.2rem 0.6rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--zs-grau);
    background: var(--zs-flaeche);
    border-radius: var(--zs-ecke);
}

/* Knopf unten, ueber dem gespannten Link. */
.kategorie__drehen {
    position: relative;
    z-index: 1;
    align-self: flex-start;
    min-height: 2.25rem;
    margin: auto 1.35rem 1.1rem;
    padding: 0.35rem 0.85rem;
    font: inherit;
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--zs-blau);
    background: var(--zs-tafel);
    border: 1px solid rgba(0, 51, 160, 0.35);
    border-radius: var(--zs-ecke);
    cursor: pointer;
}

.kategorie__drehen:hover {
    background: var(--zs-blau-hell);
}

/* Rueckseite: der Ablauf als Liste, Station klein darueber, Spruch in
   Funkschrift. */
.kategorie__hinten {
    padding: 1rem 1.25rem 0;
    border-color: rgba(0, 51, 160, 0.35);
}

.kategorie__hinten-titel {
    margin: 0 0 0.6rem;
    font-size: 0.75rem;
    font-weight: 650;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--zs-grau);
}

.kategorie__hinten-titel:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

/* Dicht gesetzt: Station und Spruch in einer Zeile, damit die
   Rueckseite nicht viel laenger wird als die Vorderseite. */
.beispiel {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    margin: 0 0 1rem;
    padding: 0;
    list-style: none;
}

.beispiel li {
    line-height: 1.4;
}

.beispiel__station {
    margin-right: 0.4rem;
    font-size: 0.75rem;
    font-weight: 650;
    color: var(--zs-blau);
}

.beispiel__station::after {
    content: ':';
}

.beispiel__spruch {
    font-family: var(--zs-funkschrift);
    font-size: 0.82rem;
}

.kategorie__hinten-fuss {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    margin: auto -1.25rem 0;
    padding: 0.7rem 1.25rem;
    background: var(--zs-flaeche);
    border-top: 1px solid var(--zs-rahmen);
}

.kategorie__zurueck {
    min-height: 2.25rem;
    padding: 0.35rem 0.5rem;
    font: inherit;
    font-size: 0.9rem;
    color: var(--zs-grau);
    background: transparent;
    border: none;
    border-radius: var(--zs-ecke);
    cursor: pointer;
}

.kategorie__zurueck:hover {
    color: var(--zs-tinte);
    text-decoration: underline;
    text-underline-offset: 3px;
}

.kategorie__ueben {
    display: inline-flex;
    align-items: center;
    min-height: 2.25rem;
    padding: 0.35rem 1rem;
    font-weight: 650;
    color: #ffffff;
    text-decoration: none;
    background: var(--zs-blau);
    border-radius: var(--zs-ecke);
}

.kategorie__ueben:hover {
    background: var(--zs-blau-dunkel);
}

/* Weniger Bewegung: nicht drehen, sondern umblenden. */
@media (prefers-reduced-motion: reduce) {
    .kategorie__innen,
    .kategorie__seite,
    .kategorie__band,
    .kategorie__pfeil {
        transition: none;
    }

    .kategorie.gedreht .kategorie__innen,
    .kategorie__hinten {
        transform: none;
    }

    .kategorie__hinten {
        visibility: hidden;
    }

    .kategorie.gedreht .kategorie__hinten {
        visibility: visible;
    }

    .kategorie.gedreht .kategorie__vorne {
        visibility: hidden;
    }

    .kategorie__vorne:hover .kategorie__pfeil {
        transform: none;
    }
}

/* ─── Uebung ──────────────────────────────────────────────── */

.schalter {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    font-size: 0.95rem;
    color: var(--zs-grau);
    cursor: pointer;
}

/* Segmentknopf: die Rollen nebeneinander in einem Rahmen, die gewaehlte
   dunkel gefuellt (aria-pressed), wie die Themenfilter im Funkgeraet. */
.segmente {
    display: inline-flex;
    flex-wrap: wrap;
    padding: 2px;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
}

.segmente button {
    min-height: 2.25rem;
    padding: 0.3rem 0.85rem;
    font: inherit;
    font-size: 0.9rem;
    color: var(--zs-tinte);
    background: transparent;
    border: none;
    border-radius: 3px;
    cursor: pointer;
}

.segmente button:hover {
    background: var(--zs-flaeche);
}

.segmente button[aria-pressed="true"] {
    font-weight: 600;
    color: #ffffff;
    background: var(--zs-tinte);
}

/* Schalter statt Haekchen: Spur mit Knopf, rechts = an. */
.schalter__knopf {
    position: relative;
    flex: none;
    width: 2.5rem;
    height: 1.4rem;
    margin: 0;
    appearance: none;
    -webkit-appearance: none;
    background: var(--zs-rahmen);
    border-radius: 999px;
    cursor: pointer;
    transition: background-color 0.15s;
}

.schalter__knopf::after {
    content: '';
    position: absolute;
    top: 3px;
    left: 3px;
    width: calc(1.4rem - 6px);
    height: calc(1.4rem - 6px);
    background: #ffffff;
    border-radius: 50%;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
    transition: transform 0.15s;
}

.schalter__knopf:checked {
    background: var(--zs-blau);
}

.schalter__knopf:checked::after {
    transform: translateX(1.1rem);
}

.schalter__knopf:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

/* Lage: hellblauer Hinweis ueber der Arbeitsflaeche. */
.lage {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.25rem 1rem;
    margin: 0 0 1rem;
    padding: 0.85rem 1.1rem;
    font-size: 1.05rem;
    font-weight: 600;
    line-height: 1.45;
    color: var(--zs-tinte);
    background: var(--zs-blau-hell);
    border-radius: var(--zs-ecke);
}

.lage__etikett {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--zs-blau);
}

/* Die Uebung liegt auf einer einzigen weissen Tafel: hier wird gearbeitet. */
.arbeitsflaeche {
    padding: 1.5rem 1.75rem 1.25rem;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke-gross);
    box-shadow: var(--zs-schatten);
}

/* Knopfleiste der Uebung als Fusszeile des Kastens, mit breiten Knoepfen
   (Skizze D). Klebt beim Scrollen unten, Pruefen bleibt erreichbar. Die
   Leiste gehoert der Uebungskomponente, darum :deep(). */
.arbeitsflaeche.mit-fussleiste :deep(.knopfleiste) {
    position: sticky;
    bottom: 0;
    z-index: 2;
    gap: 0.75rem;
    margin: 2rem -1.75rem -1.25rem;
    padding: 1rem 1.75rem;
    background: var(--zs-flaeche);
    border-top: 1px solid var(--zs-rahmen);
    border-radius: 0 0 var(--zs-ecke-gross) var(--zs-ecke-gross);
}

.arbeitsflaeche.mit-fussleiste :deep(.knopfleiste button) {
    flex: 1 1 0;
    max-width: 15rem;
    min-height: 3rem;
    font-size: 1rem;
}

a:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

/* Die Knoepfe kommen ueber den Slot in die Knopfleiste der Ansicht. Slot-
   Inhalt traegt den Scope DIESER Komponente, darum steht ihr ganzes Aussehen
   hier — die Regeln der Ansicht greifen nicht. Rechts abgesetzt, wie in der
   Vorgabe. */
.rechts {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 2.5rem;
    margin-left: auto;
    text-decoration: none;
    box-sizing: border-box;
    padding: 0.45rem 1rem;
    font: inherit;
    font-weight: 600;
    color: var(--zs-tinte);
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
    cursor: pointer;
}

.rechts:hover {
    border-color: var(--zs-tinte);
}

button:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

@media (max-width: 900px) {
    .m04 {
        padding: 1.5rem 16px 2.5rem;
    }

    .arbeitsflaeche {
        padding: 1rem;
    }

    .arbeitsflaeche.mit-fussleiste :deep(.knopfleiste) {
        margin: 1.5rem -1rem -1rem;
        padding: 0.75rem 1rem;
    }

    .modus__kopf,
    .modus__ziel {
        padding-left: 16px;
        padding-right: 16px;
    }

    .modus__reihe {
        padding: 0.75rem 16px 0;
    }

    .modus__mitte {
        padding: 1.25rem 16px 2.5rem;
    }
}

@media (prefers-reduced-motion: reduce) {
    .karte,
    .karte::after,
    .schalter__knopf,
    .schalter__knopf::after {
        transition: none;
    }

    .karte:hover {
        transform: none;
    }
}
</style>
